import React, { useState, useEffect } from 'react';
import { Table, Input, Card, Tag, Button, Modal, InputNumber, Form, message, Alert } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { SearchOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllUsers } from '../../graphql/queries';
import { createOneCredit } from '../../graphql/mutation';
import '../Audios/Audios.css';
import './Users.css';

const { Search } = Input;

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [pagination, setPagination] = useState({
    current: parseInt(searchParams.get('page')) || 1,
    pageSize: parseInt(searchParams.get('pageSize')) || 10,
  });
  const [creditModalVisible, setCreditModalVisible] = useState(false);
  const [creditModalType, setCreditModalType] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditForm] = Form.useForm();

  const { loading, data, refetch } = useQuery(getAllUsers, {
    variables: {
      page: pagination.current,
      pageSize: pagination.pageSize,
      rcUserId: searchText || null,
    },
  });

  const [addCredit, { loading: creditLoading }] = useMutation(createOneCredit, {
    onCompleted: () => {
      message.success(creditModalType === 'add' ? 'Kredi başarıyla eklendi' : 'Kredi başarıyla çıkarıldı');
      setCreditModalVisible(false);
      creditForm.resetFields();
      setSelectedUser(null);
      refetch();
    },
    onError: (error) => {
      message.error(`Hata: ${error.message}`);
    }
  });

  const handleCreditSubmit = async () => {
    try {
      const values = await creditForm.validateFields();
      const amount = creditModalType === 'add' ? values.amount : -values.amount;
      await addCredit({
        variables: {
          userId: selectedUser.id,
          amount: amount,
          date: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const openCreditModal = (type, user, e) => {
    e.stopPropagation();
    setCreditModalType(type);
    setSelectedUser(user);
    setCreditModalVisible(true);
    creditForm.resetFields();
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchText) params.set('search', searchText);
    params.set('page', pagination.current.toString());
    params.set('pageSize', pagination.pageSize.toString());
    setSearchParams(params);
  }, [pagination, searchText, setSearchParams]);

  const columns = [
    {
      title: 'RC User ID',
      dataIndex: 'rcUserId',
      key: 'rcUserId',
      ellipsis: true,
      render: (text) => text || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'isPremium',
      key: 'isPremium',
      width: 100,
      align: 'center',
      filters: [
        { text: 'Premium', value: true },
        { text: 'Regular', value: false },
      ],
      render: (isPremium) => (
        <Tag color={isPremium ? 'gold' : 'default'}>
          {isPremium ? 'Premium' : 'Regular'}
        </Tag>
      ),
    },
    {
      title: 'Credit',
      dataIndex: 'totalCredit',
      key: 'totalCredit',
      width: 160,
      render: (totalCredit, record) => (
        <div className="credit-cell">
          <span className="credit-value">
            {(totalCredit?.total || 0).toLocaleString()}
          </span>
          <div className="credit-actions">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => openCreditModal('add', record, e)}
              className="credit-btn"
            />
            <Button
              danger
              size="small"
              icon={<MinusOutlined />}
              onClick={(e) => openCreditModal('remove', record, e)}
              className="credit-btn"
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
    },
  ];

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    refetch({
      page: 1,
      pageSize: pagination.pageSize,
      rcUserId: value || null,
    });
  };

  const handleRowClick = (record) => {
    navigate(`/user/${record.id}`);
  };

  return (
    <div className="audios-container">
      <div className="audios-header">
        <p className="audios-breadcrumb">Pages / Users</p>
        <h1 className="audios-title">Users</h1>
      </div>

      <Card className="audios-card">
        <div className="filters-container">
          <Search
            placeholder="Search by RC User ID"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </div>
        <Table
            columns={columns}
            dataSource={data?.getAllUsers?.users}
            loading={loading}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              ...pagination,
              total: data?.getAllUsers?.totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} users`,
              size: 'small',
            }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
          />
      </Card>

      <Modal
        title={`${creditModalType === 'add' ? 'Kredi Ekle' : 'Kredi Çıkar'}${selectedUser ? ` - ${selectedUser.rcUserId || selectedUser.id}` : ''}`}
        open={creditModalVisible}
        onOk={handleCreditSubmit}
        onCancel={() => {
          setCreditModalVisible(false);
          creditForm.resetFields();
          setSelectedUser(null);
        }}
        confirmLoading={creditLoading}
        okText={creditModalType === 'add' ? 'Ekle' : 'Çıkar'}
        cancelText="İptal"
      >
        <Form form={creditForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Miktar"
            rules={[
              { required: true, message: 'Lütfen miktar girin' },
              { type: 'number', min: 1, message: 'Miktar en az 1 olmalıdır' }
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder={creditModalType === 'add' ? 'Eklenecek kredi miktarı' : 'Çıkarılacak kredi miktarı'}
            />
          </Form.Item>
        </Form>
        {creditModalType === 'remove' && (
          <Alert
            message="Dikkat"
            description="Bu işlem kullanıcının kredisini düşürecektir."
            type="warning"
            showIcon
          />
        )}
      </Modal>
    </div>
  );
};

export default Users; 